import * as t from "io-ts"
import { DateFromISOString } from "io-ts-types/lib/DateFromISOString"

export const milestoneStatus = t.keyof({
  Done: null,
  InProgress: null,
  ComingUp: null,
  NotYetImplemented: null,
  FailedToRetrieveData: null,
})
export type MilestoneStatusType = t.TypeOf<typeof milestoneStatus>

export const stepStatus = t.keyof({
  Done: null,
  ComingUp: null,
})
export type StepStatusType = t.TypeOf<typeof stepStatus>

export const solicitor = t.keyof({
  Juno: null,
  Unknown: null,
  Muve: null,
  Habito: null,
})
export type SolicitorType = t.TypeOf<typeof solicitor>

const milestoneStatusSchema = t.strict({
  type: milestoneStatus,
})

const stepStatusSchema = t.strict({
  type: stepStatus,
})

export const solicitorSchema = t.strict({
  type: solicitor,
})

const step = t.union([
  t.strict({ Right: stepStatusSchema }),
  t.strict({
    Left: t.strict({
      type: t.keyof({
        FailedToRetrieveData: null,
        NotYetImplemented: null,
      }),
    }),
  }),
])

export type Step = t.TypeOf<typeof step>

export const homebuyerSurveyMilestoneSchema = t.strict({
  status: milestoneStatusSchema,
  surveyType: t.strict({
    type: t.keyof({
      HomebuyersReport: null,
      HomebuyersReportWithValuation: null,
      BuildingSurvey: null,
      NoSurvey: null,
    }),
  }),
  steps: t.strict({
    arrangingSurvey: step,
    surveyBooked: t.strict({
      status: step,
      surveyDate: t.union([t.null, DateFromISOString]),
    }),
    signedTCs: step,
    surveyCompleted: step,
  }),
})

export const getHomebuyerSurveyMilestoneSteps = (
  val: t.TypeOf<typeof homebuyerSurveyMilestoneSchema>
): Step[] => [
  val.steps.arrangingSurvey,
  val.steps.surveyBooked.status,
  val.steps.signedTCs,
  val.steps.surveyCompleted,
]

export const legalWorkMilestoneSchema = t.strict({
  solicitor: solicitorSchema,
  status: milestoneStatusSchema,
  steps: t.strict({
    instructed: step,
    completed: step,
  }),
})

export const getLegalWorkMilestoneSteps = (
  val: t.TypeOf<typeof legalWorkMilestoneSchema>
): Step[] => [val.steps.instructed, val.steps.completed]

export const plusMilestonesSchema = t.strict({
  homebuyerSurveyMilestone: homebuyerSurveyMilestoneSchema,
  legalWorkMilestone: legalWorkMilestoneSchema,
})

const plusMilestonesResultSchema = t.union([
  t.strict({ Right: plusMilestonesSchema }),
  t.strict({
    Left: t.strict({
      type: t.keyof({
        FailedToRetrieveData: null,
        ApplicationNotPlus: null,
      }),
    }),
  }),
])

export const documentChecksAndValuationMilestoneSchema = t.strict({
  status: milestoneStatusSchema,
  steps: t.strict({
    lenderReceivedDocuments: step,
    lenderApprovedDocuments: step,
    lenderValuationBooked: t.strict({
      status: step,
      valuationDate: t.union([t.null, DateFromISOString]),
    }),
    lenderValuationResultsReceived: step,
  }),
})

export const getDocumentChecksAndValuationMilestoneSteps = (
  val: t.TypeOf<typeof documentChecksAndValuationMilestoneSchema>
): Step[] => [
  val.steps.lenderReceivedDocuments,
  val.steps.lenderApprovedDocuments,
  val.steps.lenderValuationBooked.status,
  val.steps.lenderValuationResultsReceived,
]

export const mortgageOfferMilestoneSchema = t.strict({
  status: milestoneStatusSchema,
  steps: t.strict({
    awaitingLenderResponse: step,
    lenderConfirmedOffer: step,
  }),
})

export const getMortgageOfferMilestoneSteps = (
  val: t.TypeOf<typeof mortgageOfferMilestoneSchema>
): Step[] => [val.steps.awaitingLenderResponse, val.steps.lenderConfirmedOffer]

export const submissionMilestoneSchema = t.strict({
  status: milestoneStatusSchema,
})

export const mortgageCompletionMilestoneSchema = t.strict({
  status: milestoneStatusSchema,
})

export const postSubmissionTimelineSchema = t.strict({
  documentChecksAndValuationMilestone:
    documentChecksAndValuationMilestoneSchema,
  plusMilestones: plusMilestonesResultSchema,
  mortgageOfferMilestone: mortgageOfferMilestoneSchema,
  submissionMilestone: submissionMilestoneSchema,
  mortgageCompletionMilestone: mortgageCompletionMilestoneSchema,
})
